import { useEffect, useRef, useState } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, FileUp, GripVertical, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { coursesAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { QUIZ_HTML_EXAMPLE, parseQuizHtml } from '../lib/quizHtml';
import { Modal } from './ui';
import { Switch, adminInput } from './adminUi';

// Comprehension check editor (s134, board A2): questions asked before a lesson
// can be completed — the KMS Reading Quiz dynamic (one right answer + a "Why").
// Questions can be typed or bulk-loaded from an .html file in the KMS format.

export const PASS_OPTIONS = [0, 50, 70, 80, 100];
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const newId = () => Math.random().toString(36).slice(2, 14);

export function emptyCheck() {
  return { enabled: false, pass_pct: 70, questions: [] };
}

export function checkFromLesson(lesson) {
  const c = lesson?.check;
  if (!c) return emptyCheck();
  return {
    enabled: Boolean(c.enabled),
    pass_pct: PASS_OPTIONS.includes(c.pass_pct) ? c.pass_pct : 70,
    questions: (c.questions || []).map((q) => ({
      id: q.id || newId(), q: q.q || '', options: [...(q.options || [])],
      correct: Number.isInteger(q.correct) ? q.correct : 0, explanation: q.explanation || '',
    })),
  };
}

export const requiredOf = (total, pct) => Math.ceil((total * pct) / 100);

function passLabel(pct, total) {
  if (pct === 0) return t('Answer all questions (no minimum)');
  if (!total) return `${pct}%`;
  return `${pct}% — ${requiredOf(total, pct)} ${t('of')} ${total} ${t('correct')}`;
}

function QuestionCard({ q, index, open, onToggle, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.id });
  const setOption = (i, value) => onChange({ ...q, options: q.options.map((o, j) => (j === i ? value : o)) });
  const removeOption = (i) => onChange({
    ...q,
    options: q.options.filter((_, j) => j !== i),
    correct: q.correct === i ? 0 : q.correct > i ? q.correct - 1 : q.correct,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : undefined }}
      className={`rounded-xl border border-lms-line bg-lms-surface ${isDragging ? 'shadow-[var(--lms-elev-2)]' : ''}`}
    >
      <div className={`flex items-center gap-2.5 py-2 pl-2 pr-2 ${open ? 'border-b border-lms-line' : ''}`}>
        <button type="button" {...attributes} {...listeners} className="flex h-9 w-8 cursor-grab touch-none items-center justify-center text-lms-faint hover:text-lms-ink" aria-label={t('Drag to reorder')}>
          <GripVertical size={16} />
        </button>
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex min-w-0 flex-1 items-center gap-3 py-1.5 text-left">
          <span className="shrink-0 text-xs font-bold uppercase tracking-[0.06em] text-lms-sub">{t('Question')} {index + 1}</span>
          {!open && <span className="min-w-0 flex-1 truncate text-sm text-lms-ink">{q.q || t('(no text yet)')}</span>}
          {!open && <span className="hidden shrink-0 text-xs text-lms-sub sm:inline">{q.options.length} {t('options')}</span>}
          <ChevronDown size={16} className={`ml-auto shrink-0 text-lms-ink transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        <button type="button" onClick={onRemove} aria-label={t('Delete question')} className="flex h-10 w-10 items-center justify-center rounded-lg text-lms-faint hover:text-[var(--lms-danger)]">
          <Trash2 size={15} />
        </button>
      </div>
      {open && (
        <div className="flex flex-col gap-3 px-4 pb-4 pt-3.5">
          <input
            value={q.q}
            onChange={(e) => onChange({ ...q, q: e.target.value })}
            placeholder={t('Write the question')}
            aria-label={t('Question')}
            className={`${adminInput} font-semibold`}
          />
          <div className="flex flex-col gap-2">
            {q.options.map((opt, i) => {
              const right = q.correct === i;
              return (
                <div key={i} className="flex items-center gap-3">
                  <input
                    type="radio"
                    name={`correct-${q.id}`}
                    checked={right}
                    onChange={() => onChange({ ...q, correct: i })}
                    aria-label={`${t('Mark correct')} ${LETTERS[i]}`}
                    className="h-[18px] w-[18px] shrink-0 accent-[var(--lms-link)]"
                  />
                  <span className={`w-5 shrink-0 text-[13px] font-bold ${right ? 'text-lms-link' : 'text-lms-sub'}`}>{LETTERS[i]}</span>
                  <input
                    value={opt}
                    onChange={(e) => setOption(i, e.target.value)}
                    placeholder={`${t('Option')} ${LETTERS[i]}`}
                    aria-label={`${t('Option')} ${LETTERS[i]}`}
                    className={`${adminInput} h-10 flex-1 ${right ? 'border-lms-accent bg-lms-tag-free-bg/40' : ''}`}
                  />
                  {right && <span className="hidden shrink-0 text-xs font-bold text-lms-tag-free-ink sm:inline">{t('Correct')}</span>}
                  {q.options.length > 2 && (
                    <button type="button" onClick={() => removeOption(i)} aria-label={`${t('Remove')} ${LETTERS[i]}`} className="flex h-9 w-9 shrink-0 items-center justify-center text-lms-faint hover:text-[var(--lms-danger)]">
                      <X size={14} />
                    </button>
                  )}
                </div>
              );
            })}
            {q.options.length < 6 && (
              <button type="button" onClick={() => onChange({ ...q, options: [...q.options, ''] })} className="ml-[62px] self-start py-1.5 text-[13px] font-bold text-lms-link hover:underline">
                + {t('Add option')}
              </button>
            )}
          </div>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12.5px] font-semibold text-lms-body">
              {t('Why — shown after they answer')} <span className="font-normal text-lms-sub">({t('optional')})</span>
            </span>
            <input value={q.explanation} onChange={(e) => onChange({ ...q, explanation: e.target.value })} className={`${adminInput} h-10`} />
          </label>
        </div>
      )}
    </div>
  );
}

export function CheckResultsModal({ lessonId, lessonTitle, onClose }) {
  const [data, setData] = useState(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    coursesAPI.checkResults(lessonId).then(({ data: d }) => setData(d)).catch(() => {
      toast.error(t('Could not load the results'));
      closeRef.current();
    });
  }, [lessonId]);
  return (
    <Modal title={`${t('Check results')} · ${lessonTitle}`} onClose={onClose} wide="xl">
      {!data ? <p className="text-sm text-lms-muted">{t('Loading…')}</p> : (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-3 gap-3">
            {[[data.members, t('Students')], [data.passed, t('Passed')], [data.attempts, t('Attempts')]].map(([n, l]) => (
              <div key={l} className="rounded-xl border border-lms-line p-4">
                <div className="text-2xl font-bold text-lms-ink lms-num">{n}</div>
                <div className="text-xs text-lms-sub">{l}</div>
              </div>
            ))}
          </div>
          {data.questions.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-bold text-lms-ink">{t('Missed per question')}</span>
              {data.questions.map((q, i) => (
                <div key={q.id} className="flex items-center gap-3 text-sm">
                  <span className="w-6 shrink-0 text-lms-sub lms-num">{i + 1}.</span>
                  <span className="min-w-0 flex-1 truncate text-lms-body">{q.q}</span>
                  <span className="h-2 w-28 shrink-0 overflow-hidden rounded-full bg-lms-raised">
                    <span className="block h-2 bg-lms-sale" style={{ width: `${q.miss_pct}%` }} />
                  </span>
                  <span className="w-11 shrink-0 text-right text-lms-sub lms-num">{q.miss_pct}%</span>
                </div>
              ))}
            </div>
          )}
          {data.rows.length === 0 ? (
            <p className="text-sm text-lms-sub">{t('Nobody has taken this check yet.')}</p>
          ) : (
            <div className="flex flex-col divide-y divide-lms-line rounded-xl border border-lms-line">
              {data.rows.map((r) => (
                <div key={r.member_id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <span className="min-w-0 flex-1 truncate text-lms-ink">{r.name || r.member_id}</span>
                  <span className="text-lms-sub lms-num">{r.attempts} {r.attempts === 1 ? t('attempt') : t('attempts')}</span>
                  <span className="w-12 text-right font-semibold text-lms-ink lms-num">{r.best}/{r.total}</span>
                  <span className={`w-20 rounded-full px-2 py-0.5 text-center text-xs font-bold ${r.passed ? 'bg-lms-tag-free-bg text-lms-tag-free-ink' : 'bg-lms-tag-alt-bg text-lms-tag-alt-ink'}`}>
                    {r.passed ? t('Passed') : t('Not yet')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default function LessonCheckEditor({ check, onChange, lessonId, lessonTitle }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [openId, setOpenId] = useState(check.questions[0]?.id || null);
  const [showFormat, setShowFormat] = useState(false);
  const [results, setResults] = useState(false);
  const fileRef = useRef(null);
  const qs = check.questions;
  const set = (patch) => onChange({ ...check, ...patch });
  const setQuestions = (questions) => set({ questions });

  const addQuestion = () => {
    const q = { id: newId(), q: '', options: ['', '', ''], correct: 0, explanation: '' };
    setQuestions([...qs, q]);
    setOpenId(q.id);
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = qs.map((q) => q.id);
    setQuestions(arrayMove(qs, ids.indexOf(active.id), ids.indexOf(over.id)));
  };

  const loadFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseQuizHtml(String(reader.result || ''));
      if (!parsed.length) {
        toast.error(t('No questions found — check the HTML format.'));
        setShowFormat(true);
        return;
      }
      const filled = qs.some((q) => q.q.trim() || q.options.some((o) => o.trim()));
      if (filled && !window.confirm(`${t('Replace the current questions with the ones in the file?')} (${parsed.length})`)) return;
      const next = parsed.map((q) => ({ ...q, id: newId() }));
      onChange({ ...check, enabled: true, questions: next });
      setOpenId(next[0].id);
      toast.success(`${parsed.length} ${t('questions loaded — review them and save the lesson.')}`);
    };
    reader.readAsText(file);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4 rounded-xl border border-lms-tag-free-bg bg-lms-tag-free-bg/40 px-4 py-3.5">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-bold text-lms-ink">{t('Ask these questions before the lesson can be completed')}</span>
          <span className="text-[12.5px] leading-relaxed text-lms-sub">
            {t('“Mark complete” becomes “Take the check”. The student unlocks the next lesson only after passing.')}
          </span>
        </div>
        <Switch checked={check.enabled} onChange={(v) => set({ enabled: v })} label={t('Comprehension check')} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-lms-body">{t('Passing score')}</span>
          <select value={check.pass_pct} onChange={(e) => set({ pass_pct: Number(e.target.value) })} className={adminInput}>
            {PASS_OPTIONS.map((p) => <option key={p} value={p}>{passLabel(p, qs.length)}</option>)}
          </select>
        </label>
        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-lms-body">{t('If they don’t pass')}</span>
          <span className="flex h-11 items-center rounded-[10px] bg-lms-raised px-3.5 text-sm text-lms-body">{t('Review the lesson and retry — unlimited')}</span>
        </div>
      </div>

      <div className="flex flex-wrap items-baseline justify-between gap-2 pt-1">
        <span className="text-[15px] font-bold text-lms-ink">
          {t('Questions')}{qs.length > 1 && <span className="text-[12.5px] font-medium text-lms-sub"> · {t('drag to reorder')}</span>}
        </span>
        <span className="text-[12.5px] text-lms-sub">{t('Pick the one correct answer per question')}</span>
      </div>

      {qs.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={qs.map((q) => q.id)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-2.5">
              {qs.map((q, i) => (
                <QuestionCard
                  key={q.id}
                  q={q}
                  index={i}
                  open={openId === q.id}
                  onToggle={() => setOpenId(openId === q.id ? null : q.id)}
                  onChange={(next) => setQuestions(qs.map((x) => (x.id === q.id ? next : x)))}
                  onRemove={() => {
                    const rest = qs.filter((x) => x.id !== q.id);
                    // last one gone = no check (the switch turns itself off)
                    set(rest.length ? { questions: rest } : { questions: rest, enabled: false });
                  }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {qs.length > 1 && (
        <button
          type="button"
          onClick={() => {
            if (!window.confirm(`${t('Delete all questions')} (${qs.length})?`)) return;
            set({ questions: [], enabled: false });
            setOpenId(null);
          }}
          className="flex items-center gap-1.5 self-start text-[13px] font-bold text-lms-sale hover:underline"
        >
          <Trash2 size={14} /> {t('Delete all questions')}
        </button>
      )}
      {qs.length === 0 && (lessonId ? (
        <p className="rounded-xl bg-lms-raised px-4 py-3 text-[13px] text-lms-sub">
          {t('No questions — the check is off. Save the lesson to apply.')}
        </p>
      ) : null)}

      <div className="grid gap-2.5 sm:grid-cols-2">
        <button type="button" onClick={addQuestion} className="lms-press flex h-12 items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed border-lms-pill text-sm font-bold text-lms-ink hover:border-lms-ink">
          <Plus size={15} /> {t('Add question')}
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} className="lms-press flex h-12 items-center justify-center gap-2 rounded-xl border border-lms-line text-sm font-bold text-lms-ink hover:border-lms-ink">
          <FileUp size={15} /> {t('Load questions from HTML')}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".html,.htm,text/html"
          className="hidden"
          onChange={(e) => { loadFile(e.target.files[0]); e.target.value = ''; }}
        />
      </div>
      <div className="flex flex-col gap-2">
        <button type="button" onClick={() => setShowFormat(!showFormat)} aria-expanded={showFormat} className="self-start text-[13px] font-bold text-lms-link hover:underline">
          {showFormat ? t('Hide the HTML format') : t('What should the HTML look like?')}
        </button>
        {showFormat && (
          <div className="flex flex-col gap-2 rounded-xl bg-lms-raised p-4 text-[12.5px] leading-relaxed text-lms-body">
            <span>{t('Same format as the KMS Reading Quiz: one block per question, the right option marked with class="correct". The text “Why” is optional.')}</span>
            <pre className="overflow-x-auto whitespace-pre rounded-lg bg-lms-surface p-3 text-[12px] text-lms-ink">{QUIZ_HTML_EXAMPLE}</pre>
          </div>
        )}
      </div>

      {lessonId && (
        <button type="button" onClick={() => setResults(true)} className="self-start text-[13.5px] font-bold text-lms-link hover:underline">
          {t('See results for this lesson')}
        </button>
      )}
      {results && <CheckResultsModal lessonId={lessonId} lessonTitle={lessonTitle} onClose={() => setResults(false)} />}
    </div>
  );
}
